import { Field, Form, Formik } from "formik";
import { z } from "zod";

import { Button } from "components/ui/Button";
import { Checkbox } from "components/ui/Checkbox";
import { Dialog, DialogFooter } from "components/ui/Dialog";

import { useCreateLocation } from "../hooks/useCreateLocation";
import { useUpdateLocation } from "../hooks/useUpdateLocation";
import styles from "./LocationFormModal.module.css";

const locationSchema = z.object({
  name: z.string().min(1, "Name is required"),
});

const validateWithZod = (schema) => (values) => {
  try {
    schema.parse(values);
    return {};
  } catch (error) {
    if (error instanceof z.ZodError) {
      return error.errors.reduce((acc, err) => {
        const path = err.path.join(".");
        acc[path] = err.message;
        return acc;
      }, {});
    }
    return {};
  }
};

export const LocationFormModal = ({
  isOpen,
  onClose,
  location,
  isOnlyLocation,
  onSuccess,
}) => {
  const createMutation = useCreateLocation();
  const updateMutation = useUpdateLocation();

  const isEditMode = !!location;
  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  const initialValues = {
    name: location?.name || "",
    addressLine1: location?.address?.addressLine1 || "",
    addressLine2: location?.address?.addressLine2 || "",
    addressLine3: location?.address?.addressLine3 || "",
    city: location?.address?.city || "",
    county: location?.address?.county || "",
    postCode: location?.address?.postCode || "",
    country: location?.address?.country || "",
    isDefault: location?.isDefault || false,
  };

  const handleSubmit = (values) => {
    const payload = {
      name: values.name,
      address: {
        addressLine1: values.addressLine1,
        addressLine2: values.addressLine2,
        addressLine3: values.addressLine3,
        city: values.city,
        county: values.county,
        postCode: values.postCode,
        country: values.country,
      },
      isDefault: values.isDefault,
    };

    if (isEditMode) {
      payload.id = location.id;
    }

    const mutation = isEditMode ? updateMutation : createMutation;
    mutation.mutate(payload, {
      onSuccess: () => {
        onSuccess?.();
        onClose();
      },
    });
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      title={isEditMode ? "Edit Location" : "Add Location"}
      width={720}
    >
      <Formik
        initialValues={initialValues}
        validate={validateWithZod(locationSchema)}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ errors, touched, submitCount, values, setFieldValue }) => (
          <Form className={styles.form}>
            <div className={styles.formGrid}>
              <div className={`${styles.fieldGroup} ${styles.fieldGroupFull}`}>
                <label htmlFor="name" className={styles.label}>
                  Name
                </label>
                <Field
                  type="text"
                  id="name"
                  name="name"
                  placeholder="e.g. Main Clinic, City Centre Branch"
                  className={`${styles.input} ${
                    errors.name && (touched.name || submitCount > 0)
                      ? styles.inputError
                      : ""
                  }`}
                />
                {errors.name && (touched.name || submitCount > 0) && (
                  <span className={styles.error}>{errors.name}</span>
                )}
              </div>

              <div className={`${styles.fieldGroup} ${styles.fieldGroupFull}`}>
                <label htmlFor="addressLine1" className={styles.label}>
                  Address Line 1
                </label>
                <Field
                  type="text"
                  id="addressLine1"
                  name="addressLine1"
                  placeholder="Enter address line 1"
                  className={styles.input}
                />
              </div>

              <div className={`${styles.fieldGroup} ${styles.fieldGroupFull}`}>
                <label htmlFor="addressLine2" className={styles.label}>
                  Address Line 2
                </label>
                <Field
                  type="text"
                  id="addressLine2"
                  name="addressLine2"
                  placeholder="Enter address line 2"
                  className={styles.input}
                />
              </div>

              <div className={`${styles.fieldGroup} ${styles.fieldGroupFull}`}>
                <label htmlFor="addressLine3" className={styles.label}>
                  Address Line 3
                </label>
                <Field
                  type="text"
                  id="addressLine3"
                  name="addressLine3"
                  placeholder="Enter address line 3"
                  className={styles.input}
                />
              </div>

              <div className={styles.fieldGroup}>
                <label htmlFor="city" className={styles.label}>
                  City
                </label>
                <Field
                  type="text"
                  id="city"
                  name="city"
                  placeholder="Enter city"
                  className={styles.input}
                />
              </div>

              <div className={styles.fieldGroup}>
                <label htmlFor="county" className={styles.label}>
                  County
                </label>
                <Field
                  type="text"
                  id="county"
                  name="county"
                  placeholder="Enter county"
                  className={styles.input}
                />
              </div>

              <div className={styles.fieldGroup}>
                <label htmlFor="postCode" className={styles.label}>
                  Post Code
                </label>
                <Field
                  type="text"
                  id="postCode"
                  name="postCode"
                  placeholder="Enter post code"
                  className={styles.input}
                />
              </div>

              <div className={styles.fieldGroup}>
                <label htmlFor="country" className={styles.label}>
                  Country
                </label>
                <Field
                  type="text"
                  id="country"
                  name="country"
                  placeholder="Enter country"
                  className={styles.input}
                />
              </div>
            </div>

            <div className={styles.checkboxList}>
              <label className={styles.checkboxLabel}>
                <Checkbox
                  checked={values.isDefault}
                  disabled={isOnlyLocation}
                  onChange={(e) => setFieldValue("isDefault", e.target.checked)}
                />
                Set as default location
              </label>
              {isOnlyLocation && (
                <p className={styles.hint}>
                  This is the only location, so it's always the default.
                </p>
              )}
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                size="large"
                onClick={onClose}
                type="button"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="large"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? "Saving..."
                  : isEditMode
                  ? "Save location"
                  : "Add location"}
              </Button>
            </DialogFooter>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};
